import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  InputAdornment,
  Divider,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useProjects } from "../contexts/ProjectContext";
import { useTranslation } from "react-i18next";

// Email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const CreateProjectForm = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { createProject } = useProjects();
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [userEmails, setUserEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [missingUsers, setMissingUsers] = useState([]);

  const handleNameChange = (e) => {
    setProjectName(e.target.value);
    if (formError) setFormError("");
  };

  const handleDescriptionChange = (e) => {
    setProjectDescription(e.target.value);
  };

  const handleEmailInputChange = (e) => {
    setEmailInput(e.target.value);
    // Clear error when typing
    if (emailError) setEmailError("");
    // Clear missing users when typing new emails
    if (missingUsers.length > 0) setMissingUsers([]);
  };

  const validateEmails = (emails) => {
    for (const email of emails) {
      if (!EMAIL_REGEX.test(email)) {
        return { isValid: false, invalidEmail: email };
      }
    }
    return { isValid: true };
  };

  const addEmails = () => {
    if (!emailInput.trim()) return;

    // Split by comma and clean up whitespace
    const emailsToAdd = emailInput
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email);

    // Validate emails
    const validation = validateEmails(emailsToAdd);

    if (!validation.isValid) {
      setEmailError(
        t("createProject.invalidEmailFormat", {
          email: validation.invalidEmail,
        }),
      );
      return;
    }

    // Remove duplicates
    const uniqueEmails = [...new Set([...userEmails, ...emailsToAdd])];

    setUserEmails(uniqueEmails);
    setEmailInput("");
    setEmailError("");
    setMissingUsers([]); // Clear missing users when new emails are added
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmails();
    }
  };

  const handleEmailBlur = () => {
    addEmails();
  };

  const handleDeleteEmail = (emailToDelete) => {
    setUserEmails(userEmails.filter((email) => email !== emailToDelete));

    // If this email was in the missing users list, remove it from there too
    if (missingUsers.includes(emailToDelete)) {
      setMissingUsers(missingUsers.filter((email) => email !== emailToDelete));
    }
  };

  const handleSubmit = async () => {
    // Validate form
    if (!projectName.trim()) {
      setFormError(t("createProject.projectNameRequired"));
      return;
    }

    try {
      setIsSubmitting(true);
      setFormError("");
      setMissingUsers([]);

      // Ensure any pending emails are added
      if (emailInput.trim()) {
        addEmails();
        // If there was an error adding emails, don't proceed
        if (emailError) return;
      }

      // Create project via API
      await createProject({
        name: projectName.trim(),
        description: projectDescription.trim(),
        teamMembers: userEmails,
      });

      // Reset form and close dialog
      resetForm();
      onClose();
    } catch (error) {
      // Check if this is a missing users error
      if (error.missingUsers && Array.isArray(error.missingUsers)) {
        setMissingUsers(error.missingUsers);
        setFormError(
          t("createProject.usersNotFound", { message: error.message }),
        );
      } else {
        setFormError(error.message || t("createProject.createError"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setProjectName("");
    setProjectDescription("");
    setUserEmails([]);
    setEmailInput("");
    setEmailError("");
    setFormError("");
    setMissingUsers([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6" component="div" fontWeight={600}>
          {t("createProject.title")}
        </Typography>
        <IconButton onClick={handleClose} size="small" disabled={isSubmitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {formError && (
          <Box sx={{ mb: 2 }}>
            <FormHelperText error>{formError}</FormHelperText>
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 1 }}>
            {t("createProject.nameLabel")}
          </Typography>
          <TextField
            fullWidth
            placeholder={t("createProject.namePlaceholder")}
            value={projectName}
            onChange={handleNameChange}
            variant="outlined"
            size="small"
            autoFocus
            required
            disabled={isSubmitting}
            InputProps={{
              sx: {
                borderRadius: 0.5,
              },
            }}
            error={!!formError && !projectName.trim()}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 1 }}>
            {t("createProject.descriptionLabel")}
          </Typography>
          <TextField
            fullWidth
            placeholder={t("createProject.descriptionPlaceholder")}
            value={projectDescription}
            onChange={handleDescriptionChange}
            variant="outlined"
            multiline
            rows={3}
            size="small"
            disabled={isSubmitting}
            InputProps={{
              sx: {
                borderRadius: 0.5,
              },
            }}
          />
        </Box>

        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 1 }}>
            {t("createProject.inviteMembers")}
          </Typography>
          <TextField
            fullWidth
            placeholder={t("createProject.emailsPlaceholder")}
            value={emailInput}
            onChange={handleEmailInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleEmailBlur}
            variant="outlined"
            size="small"
            error={!!emailError}
            disabled={isSubmitting}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonAddIcon
                    fontSize="small"
                    color={emailError ? "error" : "action"}
                  />
                </InputAdornment>
              ),
              endAdornment: emailError ? (
                <InputAdornment position="end">
                  <ErrorIcon fontSize="small" color="error" />
                </InputAdornment>
              ) : null,
              sx: {
                borderRadius: 0.5,
              },
            }}
          />
          {emailError && <FormHelperText error>{emailError}</FormHelperText>}
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
          {userEmails.map((email) => (
            <Chip
              key={email}
              label={email}
              onDelete={
                isSubmitting ? undefined : () => handleDeleteEmail(email)
              }
              size="small"
              sx={{
                borderRadius: 1,
                bgcolor: missingUsers.includes(email)
                  ? "error.main"
                  : "background.paper",
                border: "1px solid",
                borderColor: missingUsers.includes(email)
                  ? "error.main"
                  : "divider",
                color: missingUsers.includes(email) ? "white" : "text.primary",
                "& .MuiChip-deleteIcon": {
                  color: missingUsers.includes(email)
                    ? "white"
                    : "text.secondary",
                  "&:hover": {
                    color: missingUsers.includes(email)
                      ? "white"
                      : "error.main",
                  },
                },
              }}
            />
          ))}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          color="secondary"
          variant="text"
          disabled={isSubmitting}
        >
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          startIcon={
            isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <AddIcon />
            )
          }
          sx={{ borderRadius: 0.5 }}
          disabled={isSubmitting || !projectName.trim()}
        >
          {isSubmitting ? t("common.creating") : t("projects.createProject")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProjectForm;
